/**
 * Audit-event hooks (P30 / B-path).
 *
 * Reads from `audit_outbox` via the control-plane:
 *   GET /api/v1/audit/events                  (filter)
 *   GET /api/v1/audit/decision-histogram      (allow/deny/error counts, last N hours)
 *   GET /api/v1/audit/counts-by-method        (top tools by call count)
 *   GET /api/v1/audit/counts-by-hour          (line-chart bucket data)
 */
import { useQuery } from '@tanstack/react-query';

import { apiFetch, qs } from './client.js';

export interface AuditEventListItem {
  event_id: string;
  event_type: string;
  correlation_id: string;
  created_at: string;
  delivery_state: string;
  payload: Record<string, unknown>;
}

interface AuditEventListResponse {
  items: AuditEventListItem[];
}

export interface DecisionHistogram {
  allow: number;
  deny: number;
  error: number;
  unknown: number;
  window_hours: number;
}

interface CountsByMethodResponse {
  items: { method: string; count: number }[];
}

interface CountsByHourResponse {
  items: { ts: string; count: number }[];
}

export interface AuditEventFilter {
  correlation_id?: string;
  actor?: string;
  method?: string;
  decision?: 'allow' | 'deny' | 'error';
  reason?: string;
  from_ts?: string;
  to_ts?: string;
  limit?: number;
}

export function useAuditEvents(filter: AuditEventFilter = {}) {
  return useQuery({
    queryKey: ['audit', 'events', filter],
    queryFn: () => apiFetch<AuditEventListResponse>(`/api/v1/audit/events${qs(filter as never)}`),
  });
}

export function useDecisionHistogram(hours = 24) {
  return useQuery({
    queryKey: ['audit', 'decisions', hours],
    queryFn: () => apiFetch<DecisionHistogram>(`/api/v1/audit/decision-histogram?hours=${hours}`),
  });
}

export function useCountsByMethod(hours = 24, limit = 10) {
  return useQuery({
    queryKey: ['audit', 'methods', hours, limit],
    queryFn: () =>
      apiFetch<CountsByMethodResponse>(
        `/api/v1/audit/counts-by-method?hours=${hours}&limit=${limit}`,
      ),
  });
}

export function useCountsByHour(hours = 24) {
  return useQuery({
    queryKey: ['audit', 'hours', hours],
    queryFn: () => apiFetch<CountsByHourResponse>(`/api/v1/audit/counts-by-hour?hours=${hours}`),
  });
}
